import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import type { BookingPaymentMethod } from '@mds/shared/types/booking';
import {
  FACILITY_PROPOSAL_STATUSES,
  type AdminFacilityProposal,
  type FacilityProposalStatus,
} from '@mds/shared/types/proposal';
import useDebounced from '@mds/shared/hooks/use-debounced';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import { DEPOSIT_PERCENTAGE, PAYMENT_METHOD_OPTIONS, type PaymentChoice } from '@/domain/bookings';
import { eventDateLabel } from '@/domain/events';
import {
  isOpen,
  isStale,
  proposalPaymentSchema,
  proposalProblem,
  proposalStatusLabel,
  proposalStatusTone,
  quotedAmount,
  rejectionSchema,
} from '@/domain/proposals';
import { clockRange, stamp } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import { useValidation } from '@/lib/validation';
import * as proposals from '@/proposals/proposals.api';
import * as resources from '@/resources/resources.api';

const ALL = 'all';
const PAGE_SIZE = 25;
const SORTS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'bookingDate:asc', label: 'Soonest date' },
  { value: 'reference:asc', label: 'Reference' },
];

const hoursLabel = (minutes: number) => `${minutes / 60}h`;

/**
 * Guest proposals for every space configured to use the proposal workflow.
 *
 * Nothing here holds a room until the last step: review, then a quote, then
 * payment — which creates the booking in the same transaction. Each step is
 * decided against the version on screen, so a proposal someone else moved on
 * is reloaded rather than overwritten.
 */
export default function ProposalsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useSession();
  const mayManage = can('reservations.manage');
  const mayConvert = mayManage && can('payments.record');

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>(ALL);
  const [resourceId, setResourceId] = useState<string>(ALL);
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  const [sort, setSort] = useState('createdAt:desc');
  const [sortKey, direction] = sort.split(':') as [
    'createdAt' | 'bookingDate' | 'reference',
    'asc' | 'desc',
  ];

  const [openId, setOpenId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [paying, setPaying] = useState(false);
  const [choice, setChoice] = useState<PaymentChoice>('deposit');
  const [method, setMethod] = useState<BookingPaymentMethod>('cash');
  const [methodDetail, setMethodDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['proposals', { page, status, resourceId, settled, sort }],
    queryFn: () =>
      proposals.listProposals({
        page,
        limit: PAGE_SIZE,
        status: status === ALL ? undefined : (status as FacilityProposalStatus),
        resourceId: resourceId === ALL ? undefined : resourceId,
        search: settled || undefined,
        sort: sortKey,
        direction,
      }),
  });

  // The rooms proposals can be for, read off the inventory rather than typed.
  const rooms = useQuery({
    queryKey: ['resources', { includeInactive: true }],
    queryFn: () => resources.listResources({ includeInactive: true }),
    select: (all) => all.filter((r) => r.bookingMode === 'proposal'),
  });

  const detail = useQuery({
    queryKey: ['proposal', openId],
    queryFn: () => proposals.getProposal(openId as string),
    enabled: openId !== null,
  });
  const proposal = detail.data ?? null;

  const rejection = useMemo(() => ({ reason }), [reason]);
  const rejectionCheck = useValidation(rejectionSchema, rejection);
  const quote = proposal?.quote ?? null;
  // Rebuilt per quote: whether the chosen option takes anything depends on it.
  const paymentSchema = useMemo(() => proposalPaymentSchema(quote), [quote]);
  const payment = useMemo(() => ({ choice, method, methodDetail }), [choice, method, methodDetail]);
  const paymentCheck = useValidation(paymentSchema, payment);

  const rows = list.data?.data ?? [];

  const filter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPage(1);
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    await queryClient.invalidateQueries({ queryKey: ['proposal', openId] });
  };

  /**
   * Runs one transition. On success the returned proposal replaces the open
   * one; on a stale version it is reloaded so the next decision is made
   * against what is really there.
   */
  const act = async (run: (p: AdminFacilityProposal) => Promise<AdminFacilityProposal>) => {
    if (!proposal) return false;
    setBusy(true);
    setProblem(null);
    try {
      const next = await run(proposal);
      queryClient.setQueryData(['proposal', next.id], next);
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      return true;
    } catch (e) {
      setProblem(proposalProblem(e, 'That did not go through. Try again.'));
      if (isStale(e)) await refresh();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectionCheck.check()) return;
    if (await act((p) => proposals.rejectProposal(p.id, p.version, reason.trim()))) {
      setRejecting(false);
      setReason('');
    }
  };

  const convert = async () => {
    if (!quote || !paymentCheck.check()) return;
    const ok = await act((p) =>
      proposals.convertProposal(p.id, {
        expectedVersion: p.version,
        expectedRateCardVersionId: quote.rateCardVersionId,
        expectedTotalAmountMinor: quote.totalAmountMinor,
        payment: {
          amountMinor: quotedAmount(quote, choice),
          method,
          ...(method === 'other' ? { methodDetail: methodDetail.trim() } : {}),
        },
      }),
    );
    if (ok) {
      setPaying(false);
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
    }
  };

  const open = (p: AdminFacilityProposal) => {
    setProblem(null);
    setOpenId(p.id);
  };

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={list.data ? `${list.data.pagination.total} proposals` : 'Proposals'}
        action={
          <Toolbar>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Reference, name, email, organization"
              aria-label="Search proposals"
              maxLength={160}
              className={controlCls + ' w-[280px]'}
            />
            <Select
              value={status}
              onChange={filter(setStatus)}
              options={[
                { value: ALL, label: 'Every status' },
                ...FACILITY_PROPOSAL_STATUSES.map((s) => ({
                  value: s,
                  label: proposalStatusLabel(s),
                })),
              ]}
              aria-label="Filter by status"
            />
            <Select
              value={resourceId}
              onChange={filter(setResourceId)}
              options={[
                { value: ALL, label: 'Every room' },
                ...(rooms.data ?? []).map((r) => ({ value: r.id, label: r.name })),
              ]}
              aria-label="Filter by room"
            />
            <Select value={sort} onChange={filter(setSort)} options={SORTS} aria-label="Sort" />
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading proposals…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load proposals"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={
              settled || status !== ALL || resourceId !== ALL
                ? 'No proposal matches'
                : 'No proposals yet'
            }
            hint={
              settled || status !== ALL || resourceId !== ALL
                ? 'Try a different search or filter.'
                : 'Guests can propose any space configured for proposal review from the public site.'
            }
          />
        ) : (
          <Table head={['Reference', 'Guest', 'Room', 'When', 'People', 'Status', 'Received']}>
            {rows.map((p) => (
              <Row key={p.id} onClick={() => open(p)}>
                <Cell className="font-mono text-md">{p.reference}</Cell>
                <Cell>
                  <div className="flex flex-col">
                    <span className="font-semibold">{p.contact.name}</span>
                    <span className="text-md text-ink-muted">
                      {p.organizationName || p.contact.email}
                    </span>
                  </div>
                </Cell>
                <Cell>{p.resource.name}</Cell>
                <Cell className="font-mono text-md">
                  {eventDateLabel(p.bookingDate)} · {clockRange(p.startMinute, p.endMinute)}
                </Cell>
                <Cell className="font-mono text-md tabular-nums">{p.attendeeCount}</Cell>
                <Cell>
                  <Chip className={'border ' + proposalStatusTone(p.status)}>
                    {proposalStatusLabel(p.status)}
                  </Chip>
                </Cell>
                <Cell className="font-mono text-md">{stamp(p.createdAt)}</Cell>
              </Row>
            ))}
          </Table>
        )}
        <Pager pagination={list.data?.pagination} onPage={setPage} />
      </Card>

      {/* --------------------------------------------------------- detail --- */}
      <Modal
        open={openId !== null && !rejecting && !paying}
        onClose={() => setOpenId(null)}
        title={proposal ? `${proposal.reference} · ${proposal.resource.name}` : 'Proposal'}
        footer={
          proposal ? (
            <>
              {mayManage && proposal.status === 'submitted' && (
                <Button
                  disabled={busy}
                  onClick={() => void act((p) => proposals.startReview(p.id, p.version))}
                >
                  Start review
                </Button>
              )}
              {mayManage && proposal.status === 'under_review' && (
                <Button
                  disabled={busy}
                  onClick={() => void act((p) => proposals.awaitPayment(p.id, p.version))}
                >
                  Quote and ask for payment
                </Button>
              )}
              {mayConvert && proposal.status === 'awaiting_payment' && proposal.quote && (
                <Button
                  variant="mint"
                  disabled={busy}
                  onClick={() => {
                    setChoice('deposit');
                    setMethod('cash');
                    setMethodDetail('');
                    setProblem(null);
                    paymentCheck.reset();
                    setPaying(true);
                  }}
                >
                  Record payment and book
                </Button>
              )}
              {mayManage && proposal.status === 'awaiting_payment' && (
                <Button
                  variant="quiet"
                  disabled={busy}
                  onClick={() => void act((p) => proposals.awaitPayment(p.id, p.version))}
                >
                  Refresh quote
                </Button>
              )}
              {proposal.booking && (
                <Button onClick={() => navigate(`/bookings?bookingId=${proposal.booking!.id}`)}>
                  Open booking {proposal.booking.reference}
                </Button>
              )}
              {mayManage && isOpen(proposal.status) && (
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => {
                    setReason('');
                    setProblem(null);
                    rejectionCheck.reset();
                    setRejecting(true);
                  }}
                >
                  Reject
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {detail.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the proposal…</p>
        ) : detail.isError || !proposal ? (
          <Banner>
            {detail.error instanceof ApiError
              ? detail.error.message
              : 'That proposal could not be read.'}
          </Banner>
        ) : (
          <>
            {problem && <Banner>{problem}</Banner>}
            <ProposalDetail proposal={proposal} />
          </>
        )}
      </Modal>

      {/* --------------------------------------------------------- reject --- */}
      <Modal
        open={rejecting && proposal !== null}
        onClose={() => !busy && setRejecting(false)}
        title="Reject this proposal"
        footer={
          <>
            <Button variant="danger" disabled={busy} onClick={() => void reject()}>
              {busy ? 'Rejecting…' : 'Reject and email the guest'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setRejecting(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base">
          This is final. No booking or payment is made, and {proposal?.contact.name} is emailed the
          reason below.
        </p>
        <Field
          label="Reason"
          required
          hint="The guest reads this."
          {...rejectionCheck.field('reason')}
        >
          <textarea
            rows={4}
            className={inputCls}
            value={reason}
            maxLength={1000}
            placeholder="The requested format does not fit the selected room."
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Modal>

      {/* -------------------------------------------------------- convert --- */}
      <Modal
        open={paying && proposal?.quote != null}
        onClose={() => !busy && setPaying(false)}
        title="Record payment and book"
        footer={
          <>
            <Button variant="mint" disabled={busy} onClick={() => void convert()}>
              {busy ? 'Booking…' : 'Record payment and create booking'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setPaying(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        {proposal?.quote && (
          <>
            <QuoteLines quote={proposal.quote} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Taking" required {...paymentCheck.field('choice')}>
                <Select
                  variant="field"
                  className={selectCls}
                  value={choice}
                  onChange={(v) => setChoice(v as PaymentChoice)}
                  options={[
                    {
                      value: 'deposit',
                      label: `${DEPOSIT_PERCENTAGE}% deposit: ${formatCents(proposal.quote.requiredDepositAmountMinor)}`,
                    },
                    {
                      value: 'full',
                      label: `Full amount: ${formatCents(proposal.quote.totalAmountMinor)}`,
                    },
                  ]}
                  aria-label="Taking"
                />
              </Field>
              <Field label="Method" required {...paymentCheck.field('method')}>
                <Select
                  variant="field"
                  className={selectCls}
                  value={method}
                  onChange={(v) => setMethod(v as BookingPaymentMethod)}
                  options={PAYMENT_METHOD_OPTIONS}
                  aria-label="Method"
                />
              </Field>
            </div>
            {method === 'other' && (
              <Field
                label="What was it"
                required
                hint="Cheque, bank transfer, and so on."
                {...paymentCheck.field('methodDetail')}
              >
                <input
                  className={inputCls}
                  value={methodDetail}
                  maxLength={80}
                  onChange={(e) => setMethodDetail(e.target.value)}
                />
              </Field>
            )}
            <p className="m-0 text-base text-ink-muted">
              The room, hours, availability and price are checked again as this is recorded. If
              anything moved, nothing is written and the proposal reloads.
            </p>
          </>
        )}
      </Modal>
    </div>
  );
}

function ProposalDetail({ proposal: p }: { proposal: AdminFacilityProposal }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        <Chip className={'border ' + proposalStatusTone(p.status)}>
          {proposalStatusLabel(p.status)}
        </Chip>
        <Chip>Received {stamp(p.createdAt)}</Chip>
      </div>

      {p.rejectionReason && (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
          <Line label="Rejected because">{p.rejectionReason}</Line>
        </dl>
      )}

      {/* The guest's answers, in the order and wording of the public form. */}
      <section className="flex flex-col gap-3">
        <Heading>Their request</Heading>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
          <Line label="Space">{p.resource.name}</Line>
          <Line label="Date">{eventDateLabel(p.bookingDate)}</Line>
          <Line label="Time">{clockRange(p.startMinute, p.endMinute)}</Line>
          <Line label="Duration">{hoursLabel(p.durationMinutes)}</Line>
          <Line label="Attendees">
            {p.attendeeCount} of {p.resource.capacity}
          </Line>
        </dl>
        <div className="flex flex-col gap-1.5">
          <span className="font-mono text-sm text-ink-muted uppercase">What is it for?</span>
          <p className="m-0 rounded-xl border border-hairline bg-raised px-4 py-3 text-base break-words whitespace-pre-line text-ink">
            {p.purpose}
          </p>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <Heading>Contact</Heading>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
          <Line label="Full name">{p.contact.name}</Line>
          <Line label="Organization">{p.organizationName || <Empty />}</Line>
          {p.jobTitle && <Line label="Job title">{p.jobTitle}</Line>}
          <Line label="Email">
            <a
              className="text-brand-text underline-offset-2 hover:underline"
              href={`mailto:${p.contact.email}`}
            >
              {p.contact.email}
            </a>
          </Line>
          <Line label="Phone / WhatsApp">
            <a
              className="text-brand-text underline-offset-2 hover:underline"
              href={`tel:${p.contact.phone}`}
            >
              {p.contact.phone}
            </a>
          </Line>
        </dl>
      </section>

      {p.status === 'awaiting_payment' && p.quote && (
        <section className="flex flex-col gap-3">
          <Heading>Quote</Heading>
          <QuoteLines quote={p.quote} />
          <p className="m-0 text-base text-ink-muted">
            Not held. Someone else can book this time until payment is recorded here.
          </p>
        </section>
      )}

      {p.booking && (
        <section className="flex flex-col gap-3">
          <Heading>Booking</Heading>
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
            <Line label="Reference">{p.booking.reference}</Line>
            <Line label="Total">{formatCents(p.booking.totalAmountMinor)}</Line>
            <Line label="Paid">{formatCents(p.booking.paidAmountMinor)}</Line>
            <Line label="Balance">{formatCents(p.booking.balanceAmountMinor)}</Line>
          </dl>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <Heading>History</Heading>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {[...p.history].reverse().map((h) => (
            <li key={h.id} className="flex flex-col gap-0.5 border-l-2 border-hairline pl-4">
              <span className="text-base font-semibold">
                {h.fromStatus ? `${proposalStatusLabel(h.fromStatus)} → ` : ''}
                {proposalStatusLabel(h.toStatus)}
              </span>
              <span className="font-mono text-sm text-ink-muted">{stamp(h.occurredAt)}</span>
              {h.note && <span className="text-base text-ink-secondary">{h.note}</span>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function QuoteLines({ quote }: { quote: NonNullable<AdminFacilityProposal['quote']> }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-hairline bg-raised px-4 py-3 text-base">
      <div className="flex justify-between gap-4">
        <span className="text-ink-muted">
          {quote.itemName} · {quote.quantity} × {formatCents(quote.unitAmountMinor)}
        </span>
        <span className="font-semibold tabular-nums">{formatCents(quote.totalAmountMinor)}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-ink-muted">{DEPOSIT_PERCENTAGE}% deposit</span>
        <span className="tabular-nums">{formatCents(quote.requiredDepositAmountMinor)}</span>
      </div>
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="m-0 border-b border-hairline pb-2 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
      {children}
    </h3>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-mono text-sm text-ink-muted uppercase">{label}</dt>
      <dd className="m-0 break-words">{children}</dd>
    </>
  );
}

/** An optional answer the guest left blank. */
function Empty() {
  return <span className="text-ink-muted">Not given</span>;
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}
